import { useQuery, useQueries, UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import { satisfactionApi } from '../services/apiService';
import { useTimeFrame } from '../contexts/TimeFrameContext';
import { logger } from '../utils/logger';
import { 
  ShiftSatisfactionData, 
  OrderTypeSatisfactionData, 
  DetailedSatisfactionData,
  CategorySatisfaction,
  SatisfactionSummary,
  TimePeriod
} from '../types/api';

// Query keys for cache management
export const SATISFACTION_QUERY_KEYS = {
  KPIS: 'satisfactionKpis',
  OVERALL_TREND: 'satisfactionOverallTrend',
  BY_SHIFT: 'satisfactionByShift',
  BY_ORDER_TYPE: 'satisfactionByOrderType',
  DETAILED: 'detailedSatisfaction',
  BY_CATEGORY: 'satisfactionByCategory',
  CATEGORIES: 'satisfactionCategories',
  SUMMARY: 'satisfactionSummary',
};

/**
 * Hook to fetch satisfaction KPIs
 */
export function useSatisfactionKpis(branchId: number | undefined, customTimeRange?: TimePeriod) {
  const { apiTimeFrame } = useTimeFrame();
  const timeRange = customTimeRange || apiTimeFrame;

  return useQuery({
    queryKey: [SATISFACTION_QUERY_KEYS.KPIS, branchId, timeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      const response = await satisfactionApi.getKpis(branchId, timeRange);
      return response.data;
    },
    enabled: !!branchId,
  });
}

/**
 * Hook to fetch overall satisfaction trend
 */
export function useSatisfactionOverallTrend(branchId: number | undefined, customTimeRange?: TimePeriod) {
  const { apiTimeFrame } = useTimeFrame();
  const timeRange = customTimeRange || apiTimeFrame;

  return useQuery({
    queryKey: [SATISFACTION_QUERY_KEYS.OVERALL_TREND, branchId, timeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      const response = await satisfactionApi.getOverallTrend(branchId, timeRange);
      return response.data;
    },
    enabled: !!branchId,
  });
}

/**
 * Hook to fetch satisfaction data by shift
 */
export function useSatisfactionByShift(branchId: number | undefined, customTimeRange?: TimePeriod) {
  // Use the timeFrame from context
  const { apiTimeFrame } = useTimeFrame();
  
  // Use custom time range if provided, otherwise use the one from context
  const timeRange = customTimeRange || apiTimeFrame;

  return useQuery({
    queryKey: [SATISFACTION_QUERY_KEYS.BY_SHIFT, branchId, timeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      const response = await satisfactionApi.getByShift(branchId, timeRange);
      return response.data;
    },
    enabled: !!branchId,
  });
}

/**
 * Hook to fetch satisfaction data by order type
 */
export function useSatisfactionByOrderType(branchId: number | undefined, customTimeRange?: TimePeriod) {
  // Use the timeFrame from context
  const { apiTimeFrame } = useTimeFrame();
  
  // Use custom time range if provided, otherwise use the one from context
  const timeRange = customTimeRange || apiTimeFrame;

  return useQuery({
    queryKey: [SATISFACTION_QUERY_KEYS.BY_ORDER_TYPE, branchId, timeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      const response = await satisfactionApi.getByOrderType(branchId, timeRange);
      return response.data;
    },
    enabled: !!branchId,
  });
}

/**
 * Hook to fetch detailed satisfaction data
 */
export function useDetailedSatisfaction(branchId: number | undefined, customTimeRange?: TimePeriod) {
  // Use the timeFrame from context
  const { apiTimeFrame } = useTimeFrame();
  
  // Use custom time range if provided, otherwise use the one from context
  const timeRange = customTimeRange || apiTimeFrame;

  return useQuery({
    queryKey: [SATISFACTION_QUERY_KEYS.DETAILED, branchId, timeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      try {
        const response = await satisfactionApi.getDetailed(branchId, timeRange);
        return response.data;
      } catch (error) {
        logger.info('Detailed satisfaction data not available', 'SatisfactionQueries', error);
        return [];
      }
    },
    enabled: !!branchId,
    // This endpoint might fail in some environments, so we'll handle it gracefully
    retry: 1
  });
}

/**
 * Hook to fetch satisfaction data by category
 */
export function useSatisfactionByCategory(branchId: number | undefined, customTimeRange?: TimePeriod) {
  // Use the timeFrame from context
  const { apiTimeFrame } = useTimeFrame();
  
  // Use custom time range if provided, otherwise use the one from context
  const timeRange = customTimeRange || apiTimeFrame;

  return useQuery({
    queryKey: [SATISFACTION_QUERY_KEYS.BY_CATEGORY, branchId, timeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      const response = await satisfactionApi.getByCategory(branchId, timeRange);
      return response.data;
    },
    enabled: !!branchId,
  });
}

/**
 * Hook to fetch satisfaction categories
 */
export function useSatisfactionCategories(branchId?: number) {
  return useQuery({
    queryKey: [SATISFACTION_QUERY_KEYS.CATEGORIES, branchId],
    queryFn: async () => {
      const response = await satisfactionApi.getCategories(branchId);
      return response.data;
    },
  });
}

/**
 * Combined hook to replace the original useSatisfactionData
 * Fetches all satisfaction-related data in parallel and calculates summary stats
 */
export function useSatisfactionData(branchId: number | undefined, customTimeRange?: TimePeriod) {
  // Use the timeFrame from context
  const { apiTimeFrame } = useTimeFrame();
  
  // Use custom time range if provided, otherwise use the one from context
  const timeRange = customTimeRange || apiTimeFrame;
  
  // Fetch all data in parallel using useQueries
  const results = useQueries({
    queries: [
      {
        queryKey: [SATISFACTION_QUERY_KEYS.BY_SHIFT, branchId, timeRange],
        queryFn: async () => {
          if (!branchId) throw new Error('Branch ID is required');
          const response = await satisfactionApi.getByShift(branchId, timeRange);
          return response.data;
        },
        enabled: !!branchId,
      },
      {
        queryKey: [SATISFACTION_QUERY_KEYS.BY_ORDER_TYPE, branchId, timeRange],
        queryFn: async () => {
          if (!branchId) throw new Error('Branch ID is required');
          const response = await satisfactionApi.getByOrderType(branchId, timeRange);
          return response.data;
        },
        enabled: !!branchId,
      },
      {
        queryKey: [SATISFACTION_QUERY_KEYS.DETAILED, branchId, timeRange],
        queryFn: async () => {
          if (!branchId) throw new Error('Branch ID is required');
          try {
            const response = await satisfactionApi.getDetailed(branchId, timeRange);
            return response.data;
          } catch (error) {
            logger.info('Detailed satisfaction data not available', 'SatisfactionQueries', error);
            return [];
          }
        },
        enabled: !!branchId,
        retry: 1
      }
    ]
  });
  
  // Extract individual query results
  const [
    shiftDataQuery,
    orderTypeDataQuery,
    detailedDataQuery,
  ] = results as [
    UseQueryResult<ShiftSatisfactionData[]>,
    UseQueryResult<OrderTypeSatisfactionData[]>,
    UseQueryResult<DetailedSatisfactionData[]>
  ];
  
  // Calculate summary statistics from the data
  const summaryStats = useMemo<SatisfactionSummary>(() => {
    const shiftData = shiftDataQuery.data || [];
    const orderTypeData = orderTypeDataQuery.data || [];
    
    if (shiftData.length === 0 && orderTypeData.length === 0) {
      return {
        avgSatisfaction: 0,
        totalSurveys: 0,
        bestShift: '',
        bestShiftSatisfaction: 0,
        bestOrderType: '',
        bestOrderTypeSatisfaction: 0
      };
    }
    
    const allSatisfactionData = [
      ...shiftData.map(d => d.satisfaction_percentage),
      ...orderTypeData.map(d => d.satisfaction_percentage)
    ].filter(value => !isNaN(value));
    
    const avgSatisfaction = allSatisfactionData.length 
      ? allSatisfactionData.reduce((sum, val) => sum + val, 0) / allSatisfactionData.length 
      : 0;
    
    const totalShiftSurveys = shiftData.reduce((sum, d) => sum + d.total_surveys, 0);
    const totalOrderSurveys = orderTypeData.reduce((sum, d) => sum + d.total_surveys, 0);
    
    // Find best performing shift
    const shiftAvgs = shiftData.reduce((acc: Record<string, { total: number, count: number }>, d) => {
      if (!acc[d.shift]) {
        acc[d.shift] = { total: 0, count: 0 };
      }
      acc[d.shift].total += d.satisfaction_percentage;
      acc[d.shift].count += 1;
      return acc;
    }, {});
    
    let bestShift = '';
    let bestShiftSatisfaction = 0;
    
    Object.entries(shiftAvgs).forEach(([shift, data]) => {
      const avg = data.total / data.count;
      if (avg > bestShiftSatisfaction) {
        bestShiftSatisfaction = avg;
        bestShift = shift;
      }
    });
    
    // Find best performing order type
    const orderTypeAvgs = orderTypeData.reduce((acc: Record<string, { total: number, count: number }>, d) => {
      if (!acc[d.order_type]) {
        acc[d.order_type] = { total: 0, count: 0 };
      }
      acc[d.order_type].total += d.satisfaction_percentage;
      acc[d.order_type].count += 1;
      return acc;
    }, {});
    
    let bestOrderType = '';
    let bestOrderTypeSatisfaction = 0;
    
    Object.entries(orderTypeAvgs).forEach(([orderType, data]) => {
      const avg = data.total / data.count;
      if (avg > bestOrderTypeSatisfaction) {
        bestOrderTypeSatisfaction = avg;
        bestOrderType = orderType;
      }
    });
    
    return {
      avgSatisfaction,
      totalSurveys: totalShiftSurveys + totalOrderSurveys,
      bestShift,
      bestShiftSatisfaction,
      bestOrderType,
      bestOrderTypeSatisfaction
    };
  }, [shiftDataQuery.data, orderTypeDataQuery.data]);
  
  // Determine if any query is loading or has error
  const isLoading = shiftDataQuery.isLoading || orderTypeDataQuery.isLoading || detailedDataQuery.isLoading;
  const error = shiftDataQuery.error || orderTypeDataQuery.error || null;
  
  return {
    // Data
    shiftData: shiftDataQuery.data || [],
    orderTypeData: orderTypeDataQuery.data || [],
    detailedSatisfactionData: detailedDataQuery.data || [],
    summaryStats,
    
    // Status
    loading: isLoading,
    error: error ? (error as Error).message : null,
    
    // Helper functions
    refetch: () => {
      shiftDataQuery.refetch();
      orderTypeDataQuery.refetch();
      detailedDataQuery.refetch();
    }
  };
}